import React, { useState, useEffect } from 'react';
import { Dumbbell, Clock, Star, Award, Zap, CheckCircle, AlertCircle, Users, TrendingUp, Calendar, DollarSign, Activity, BarChart3, User, LogOut, Plus, Trash2, X, ArrowRight, Flame, Target, Heart, GraduationCap, BookOpen, Mail, Linkedin } from 'lucide-react';
import Login from './Login';
import InvestorWelcome from './InvestorWelcome';

// Enhanced Logo Header Component - Used on all pages
const LogoHeader = ({ className = "", showBadge = false, lightBackground = false }) => (
  <div className={`flex items-center justify-center gap-3 mb-4 ${className}`}>
    <div className="relative">
      <img 
        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Ccircle cx='100' cy='100' r='95' fill='%23003DA5' stroke='none'/%3E%3Ccircle cx='100' cy='100' r='75' fill='%23FF6633' stroke='none'/%3E%3Ccircle cx='70' cy='100' r='25' fill='%23003DA5' stroke='none'/%3E%3Ccircle cx='130' cy='100' r='25' fill='%23003DA5' stroke='none'/%3E%3Ccircle cx='100' cy='100' r='8' fill='%23FFCC00' stroke='none'/%3E%3C/svg%3E" 
        alt="RepMingle Logo" 
        className="h-12 md:h-16 w-auto drop-shadow-lg"
      />
      {showBadge && (
        <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full text-xs font-bold whitespace-nowrap">
          ⭐ PATENT PENDING
        </div>
      )}
    </div>
    <div className="text-left">
      <h1 className={`text-3xl md:text-4xl font-black leading-tight drop-shadow-lg ${lightBackground ? 'text-gray-800' : 'text-white'}`}>RepMingle</h1>
      <p className={`text-sm md:text-base font-bold drop-shadow-lg ${lightBackground ? 'text-blue-600' : 'text-yellow-400'}`}>Stop Guessing! Start Training!</p>
    </div>
  </div>
);

// PASSWORD PROTECTION WRAPPER
const PasswordProtectedApp = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const authStatus = sessionStorage.getItem('repMingleAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setShowWelcome(true);
    }
  }, []);

  const handleLogin = (status) => {
    setIsAuthenticated(status);
    sessionStorage.setItem('repMingleAuth', 'true');
    setShowWelcome(true);
  };

  const handleDismissWelcome = () => {
    setShowWelcome(false);
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <>
      <RepMingleSystem isAuthenticated={isAuthenticated} />
      {showWelcome && <InvestorWelcome onDismiss={handleDismissWelcome} />}
    </>
  );
};

// Mock data
const initialTrainers = [
  {
    id: 1,
    name: "Mike Thompson",
    email: "mike.thompson@repmingle.com",
    password: "trainer123",
    specializations: ["Olympic Weightlifting", "Strength Training", "Powerlifting"],
    rating: 4.9,
    sessionsToday: 2,
    certifications: ["USAW Level 2", "CSCS"],
    experience: "Advanced",
    image: "👨‍🏫",
    available: true,
    schedule: [
      { time: "09:00 AM", client: "John Doe", type: "Strength Training" },
      { time: "11:00 AM", client: "Available", type: "" }
    ],
    offTimes: []
  },
  {
    id: 2,
    name: "Sarah Chen",
    email: "sarah.chen@repmingle.com",
    password: "trainer123",
    specializations: ["Bodybuilding", "Hypertrophy", "Muscle Building"],
    rating: 4.8,
    sessionsToday: 1,
    certifications: ["NASM-CPT", "Precision Nutrition"],
    experience: "Intermediate/Advanced",
    image: "👩‍🏫",
    available: true,
    schedule: [
      { time: "10:00 AM", client: "Available", type: "" }
    ],
    offTimes: []
  },
  {
    id: 3,
    name: "Robert Martinez",
    email: "robert.martinez@repmingle.com",
    password: "trainer123",
    specializations: ["Senior Fitness", "Functional Training", "Mobility"],
    rating: 5.0,
    sessionsToday: 0,
    certifications: ["ACE Senior Fitness", "FMS"],
    experience: "All Levels",
    image: "👨‍⚕️",
    available: true,
    schedule: [],
    offTimes: []
  }
];

const existingMembers = [
  {
    id: "12345",
    name: "Alex Johnson",
    membershipType: "Premium",
    experienceLevel: "Intermediate",
    healthScreened: true
  },
  {
    id: "67890",
    name: "Jamie Lee",
    membershipType: "Basic",
    experienceLevel: "Beginner",
    healthScreened: true
  }
];

// Enhanced promotional goals with better UX
const promotionalGoals = [
  {
    title: "Perfect Your Deadlift",
    subtitle: "Unlock Glute & Hamstring Power—Today",
    salesMessage: "Form Expert Standing By!",
    goal: "olympic_lifting",
    bgColor: "from-red-600 to-orange-500",
    icon: "🏋️",
    specialty: "Olympic Weightlifting",
    emoji: "🎯"
  },
  {
    title: "Arm Day: Unleash Your Pump",
    subtitle: "21s Curls + Tricep Supersets",
    salesMessage: "Biceps Specialist Available NOW",
    goal: "muscle_building",
    bgColor: "from-purple-600 to-pink-500",
    icon: "💪",
    specialty: "Bodybuilding",
    emoji: "⚡"
  },
  {
    title: "Safe Strength for 50+",
    subtitle: "Chair Squats & Balance Boost",
    salesMessage: "Senior Fitness Pro Ready for Support",
    goal: "senior_fitness",
    bgColor: "from-blue-600 to-cyan-500",
    icon: "🧘",
    specialty: "Senior Fitness",
    emoji: "🧘"
  },
  {
    title: "Shred Session: Burn Faster",
    subtitle: "Learn: Burpees, Mountain Climbers & Sprints",
    salesMessage: "Fat Loss Coach Here NOW - Transform Today—Tap to Begin!",
    goal: "hiit",
    bgColor: "from-green-600 to-emerald-500",
    icon: "🔥",
    specialty: "HIIT",
    emoji: "🔥"
  },
  {
    title: "Jump Higher—Quick Results",
    subtitle: "Vertical Box Jump & Plyometric Explode",
    salesMessage: "Performance Coach On Floor NOW",
    goal: "sports",
    bgColor: "from-indigo-600 to-blue-500",
    icon: "⚡",
    specialty: "CrossFit",
    emoji: "💥"
  },
  {
    title: "Hip Flexor Relief NOW",
    subtitle: "Pigeon Pose & Lower Back Fix",
    salesMessage: "Mobility Expert Available—$45!",
    goal: "yoga",
    bgColor: "from-pink-600 to-rose-500",
    icon: "🧘‍♀️",
    specialty: "Yoga",
    emoji: "🌸"
  }
];

const boostMessages = [
  "🎯 The best time to train is NOW",
  "💪 Small sessions, BIG results",
  "⚡ Your future self will thank you",
  "🔥 30 minutes to transform your day",
  "✨ Experts are waiting for YOU",
  "🚀 Every rep counts, start today",
  "💎 Invest in yourself, one session at a time",
  "🎪 Make training your daily habit"
];

// MAIN COMPONENT
const RepMingleSystem = ({ isAuthenticated }) => {
  const [mode, setMode] = useState('kiosk');
  const [trainers, setTrainers] = useState(initialTrainers);
  const [sessions, setSessions] = useState([
    { id: 1, member: "Alex Johnson", trainer: "Mike Thompson", time: "09:00 AM", goal: "Olympic Lifts", status: "ongoing", price: 45 },
    { id: 2, member: "Jamie Lee", trainer: "Sarah Chen", time: "10:00 AM", goal: "Muscle Building", status: "completed", price: 45 }
  ]);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [pendingTrainers, setPendingTrainers] = useState([]);

  // Kiosk states
  const [stage, setStage] = useState('attract');
  const [currentBoostIndex, setCurrentBoostIndex] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [memberData, setMemberData] = useState(null);
  const [memberId, setMemberId] = useState('12345'); // Pre-filled for demo
  const [healthScreening, setHealthScreening] = useState({
    heartCondition: null,
    chestPain: null,
    jointIssues: null,
    medications: null,
    dizziness: null
  });
  const [selectedTiming, setSelectedTiming] = useState(null);
  const [matchedTrainer, setMatchedTrainer] = useState(null);
  const [sessionPrice] = useState(45);
  const [matchCountdown, setMatchCountdown] = useState(7);
  const [newMemberData, setNewMemberData] = useState({
    firstName: 'Jane',
    lastName: 'Doe',
    email: 'jane.doe@email.com',
    phone: '(555) 987-6543'
  });

  const [trainerAuthMode, setTrainerAuthMode] = useState('signin');
  const [trainerEmail, setTrainerEmail] = useState('');
  const [trainerPassword, setTrainerPassword] = useState('');
  const [trainerDailyFocus, setTrainerDailyFocus] = useState({});
  const [trainerDailyRecommendation, setTrainerDailyRecommendation] = useState({});
  const [newTrainerData, setNewTrainerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    specializations: [],
    certifications: [],
    bio: ''
  });

  // Pre-filled sample member data for easy demo
  const sampleMemberData = {
    firstName: 'Jane',
    lastName: 'Doe',
    email: 'jane.doe@email.com',
    phone: '(555) 987-6543'
  };

  const availableSpecializations = [
    "Olympic Weightlifting", "Strength Training", "Powerlifting",
    "Bodybuilding", "Hypertrophy", "Muscle Building",
    "Senior Fitness", "Functional Training", "Mobility",
    "HIIT", "Weight Loss", "Cardio Conditioning",
    "CrossFit", "Athletic Performance", "Sports Training",
    "Yoga", "Pilates", "Flexibility Training",
    "Rehabilitation", "Corrective Exercise"
  ];

  const availableCertifications = [
    "ACSM (American College of Sports Medicine)",
    "NASM-CPT (National Academy of Sports Medicine)",
    "ACE-CPT (American Council on Exercise)",
    "NSCA-CPT (National Strength and Conditioning Association)",
    "CSCS (Certified Strength and Conditioning Specialist)",
    "ISSA (International Sports Sciences Association)",
    "CrossFit Level 1", "CrossFit Level 2",
    "USAW (USA Weightlifting)",
    "RYT-200 (Registered Yoga Teacher)",
    "RYT-500 (Registered Yoga Teacher)",
    "Precision Nutrition", "TRX Certified",
    "FMS (Functional Movement Screen)",
    "ACE Senior Fitness", "Pilates Mat Certified", "Other"
  ];

  // Rotate boost messages
  useEffect(() => {
    if (mode === 'kiosk') {
      const timer = setInterval(() => {
        setCurrentBoostIndex((prev) => (prev + 1) % boostMessages.length);
      }, 7000);
      return () => clearInterval(timer);
    }
  }, [mode]);

  // Countdown timer for matching
  useEffect(() => {
    if (stage === 'matching' && matchCountdown > 0) {
      const timer = setTimeout(() => {
        setMatchCountdown(matchCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [stage, matchCountdown]);

  // Auto-reset kiosk
  useEffect(() => {
    if (mode === 'kiosk' && stage !== 'attract' && stage !== 'confirmed') {
      const resetTimer = setTimeout(() => {
        resetKiosk();
      }, 60000);
      return () => clearTimeout(resetTimer);
    }
  }, [mode, stage]);

  const resetKiosk = () => {
    setStage('attract');
    setSelectedGoal(null);
    setMemberData(null);
    setMemberId('');
    setHealthScreening({
      heartCondition: null,
      chestPain: null,
      jointIssues: null,
      medications: null,
      dizziness: null
    });
    setSelectedTiming(null);
    setMatchedTrainer(null);
    setMatchCountdown(7);
    setNewMemberData({
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };

  const handleGoalSelection = (promo) => {
    setSelectedGoal(promo);
    setStage('identify'); // FIX #2: Direct flow
  };

  // FIX #2: Enhanced member lookup with better UX
  const handleMemberLookup = () => {
    if (memberId.length >= 4) {
      const existingMember = existingMembers.find(m => m.id === memberId);
      
      if (existingMember) {
        setMemberData(existingMember);
        setStage('session-option');
      } else {
        alert('Member ID not found. Please register as a new member.');
        setMemberId('');
      }
    }
  };

  const handleNewMemberRegistration = () => {
    if (!newMemberData.firstName || !newMemberData.lastName || 
        !newMemberData.email || !newMemberData.phone) {
      alert('Please fill in all fields');
      return;
    }
    
    const newMember = {
      id: 'new-' + Date.now(),
      name: `${newMemberData.firstName} ${newMemberData.lastName}`,
      email: newMemberData.email,
      phone: newMemberData.phone,
      membershipType: "New Member",
      experienceLevel: "Beginner",
      healthScreened: false
    };
    
    setMemberData(newMember);
    setStage('health');
  };

  const handleHealthScreening = () => {
    const responses = Object.values(healthScreening);
    const allAnswered = responses.every(r => r !== null);
    
    if (allAnswered) {
      const hasRisks = responses.some(r => r === true);
      if (hasRisks) {
        alert('Based on your responses, we recommend consulting with the trainer before starting.');
      }
      setStage('session-option');
    }
  };

  const handleSingleSessionPurchase = () => {
    setStage('payment');
  };

  const handlePayment = () => {
    setTimeout(() => {
      setStage('timing');
    }, 1500);
  };

  const handleTimingSelection = (timing) => {
    setSelectedTiming(timing);
    matchTrainer();
  };

  const calculateMeetingTime = () => {
    // Get current time from the session creation time
    const now = new Date();
    let meetingTime = new Date(now);
    let walkTime = 0;
    let warmupInfo = '';

    if (selectedTiming === 'now') {
      // No warm-up: Meet in 2 minutes
      walkTime = 2;
      warmupInfo = 'NO warm-up';
      meetingTime.setMinutes(meetingTime.getMinutes() + 2);
    } else if (selectedTiming === '15min') {
      // 15 min warm-up + 2 min walk = 17 min
      walkTime = 2;
      warmupInfo = '15 minutes warm-up';
      meetingTime.setMinutes(meetingTime.getMinutes() + 17);
    } else if (selectedTiming === '30min') {
      // 30 min warm-up + 4 min walk = 34 min
      walkTime = 4;
      warmupInfo = '30 minutes warm-up';
      meetingTime.setMinutes(meetingTime.getMinutes() + 34);
    }

    const timeString = meetingTime.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });

    return { timeString, warmupInfo, walkTime };
  };

  const matchTrainer = () => {
    setStage('matching');
    setMatchCountdown(7);
    
    setTimeout(() => {
      const availableTrainers = trainers.filter(t => t.available);
      const matchingTrainers = availableTrainers.filter(trainer =>
        trainer.specializations.some(spec => 
          selectedGoal.specialty.toLowerCase().includes(spec.toLowerCase().split(' ')[0])
        )
      );
      
      const scored = matchingTrainers.map(trainer => ({
        trainer,
        score: (trainer.rating * 0.4) + 
               ((3 - trainer.sessionsToday) * 0.3) + 
               (trainer.specializations.length * 0.3)
      }));
      
      scored.sort((a, b) => b.score - a.score);
      
      const matched = scored[0]?.trainer || availableTrainers[0];
      setMatchedTrainer(matched);
      
      const newSession = {
        id: sessions.length + 1,
        member: memberData.name,
        trainer: matched.name,
        time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        goal: selectedGoal.title,
        status: 'ongoing',
        price: sessionPrice
      };
      setSessions([...sessions, newSession]);
      
      setStage('confirmed');
    }, 7000);
  };

  // Trainer handlers
  const handleTrainerSignIn = () => {
    // Use the values from inputs, or default to test trainer
    const email = trainerEmail.trim() || 'mike.thompson@repmingle.com';
    const password = trainerPassword.trim() || 'trainer123';
    
    const trainer = trainers.find(t => t.email === email && t.password === password);
    if (trainer) {
      setSelectedTrainer(trainer);
      setTrainerAuthMode('dashboard');
    } else {
      alert('Invalid credentials. Please try again.');
    }
  };

  const handleTrainerSignUp = () => {
    if (!newTrainerData.firstName || !newTrainerData.lastName || !newTrainerData.email || 
        !newTrainerData.phone || !newTrainerData.password ||
        newTrainerData.specializations.length === 0 || 
        newTrainerData.certifications.length === 0) {
      alert('Please fill in all required fields');
      return;
    }

    const pendingTrainer = {
      id: trainers.length + pendingTrainers.length + 1,
      name: `${newTrainerData.firstName} ${newTrainerData.lastName}`,
      email: newTrainerData.email,
      phone: newTrainerData.phone,
      specializations: newTrainerData.specializations,
      certifications: newTrainerData.certifications,
      status: 'pending',
      registeredDate: new Date().toISOString().split('T')[0],
      bio: newTrainerData.bio
    };

    setPendingTrainers([...pendingTrainers, pendingTrainer]);
    alert('Registration submitted! Please wait for admin approval.');
    
    setNewTrainerData({
      firstName: '', lastName: '', email: '', password: '', phone: '',
      specializations: [], certifications: [], bio: ''
    });
    setTrainerAuthMode('signin');
  };

  const toggleSpecialization = (spec) => {
    setNewTrainerData(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec]
    }));
  };

  const toggleCertification = (cert) => {
    setNewTrainerData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  const handleApproveTrainer = (trainerId) => {
    const trainer = pendingTrainers.find(t => t.id === trainerId);
    if (trainer) {
      const newTrainer = {
        ...trainer,
        password: 'trainer123',
        rating: 5.0,
        sessionsToday: 0,
        experience: "All Levels",
        image: "👨‍🏫",
        available: false,
        schedule: [],
        offTimes: [],
        status: 'approved'
      };
      
      setTrainers([...trainers, newTrainer]);
      setPendingTrainers(pendingTrainers.filter(t => t.id !== trainerId));
      alert(`${trainer.name} has been approved!`);
    }
  };

  const handleRejectTrainer = (trainerId) => {
    const trainer = pendingTrainers.find(t => t.id === trainerId);
    if (trainer && window.confirm(`Reject ${trainer.name}?`)) {
      setPendingTrainers(pendingTrainers.filter(t => t.id !== trainerId));
    }
  };

  // ===== KIOSK MODE - ATTRACT =====
  if (mode === 'kiosk' && stage === 'attract') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-8 relative overflow-hidden">
        {/* Background animation */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>

        <div className="relative z-10">
          <LogoHeader className="mb-6" />
          
          <div className="text-center mb-8 animate-fadeIn">
            <p className="text-3xl font-semibold text-blue-400 mb-2">30 Minute Sessions</p>
            <p className="text-2xl text-gray-300 italic mb-4 font-light">"Your fitness journey, one rep at a time"</p>
            <div className="h-12 flex items-center justify-center">
              <p className="text-2xl font-semibold text-yellow-400 animate-pulse">
                {boostMessages[currentBoostIndex]}
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-6 justify-center sm:justify-end">
            <button onClick={() => setMode('admin')} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold shadow-lg text-sm sm:text-base">
              👨‍💼 Admin
            </button>
            <button onClick={() => setMode('trainer')} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold shadow-lg text-sm sm:text-base">
              👨‍🏫 Trainer
            </button>
            <button onClick={() => setMode('about')} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all font-semibold shadow-lg text-sm sm:text-base">
              👨‍🔬 About Me
            </button>
          </div>

          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {promotionalGoals.map((promo, idx) => (
              <div
                key={idx}
                onClick={() => handleGoalSelection(promo)}
                className={`bg-gradient-to-br ${promo.bgColor} rounded-2xl shadow-xl p-6 cursor-pointer transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:z-20 group relative overflow-hidden`}
              >
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="text-center relative z-10">
                  <div className="text-5xl mb-3 group-hover:scale-125 transition-transform duration-300">{promo.icon}</div>
                  <h3 className="text-white text-xl font-bold mb-2">{promo.title}</h3>
                  <p className="text-white/90 text-sm font-medium mb-2">{promo.subtitle}</p>
                  <div className="bg-white/30 backdrop-blur-sm rounded-lg px-3 py-2 mb-2">
                    <p className="text-white text-xs font-bold uppercase">{promo.salesMessage}</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-1 text-white text-sm font-semibold group-hover:bg-white/40 transition-colors">
                    Tap to Start
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 text-gray-400 text-xl animate-fadeIn">
            <p>Choose your goal to begin your 30-minute session</p>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - MEMBER IDENTIFICATION (FIX #2) =====
  if (mode === 'kiosk' && stage === 'identify') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 md:p-8 backdrop-blur-sm">
            <button 
              onClick={resetKiosk}
              className="mb-4 md:mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-colors text-sm md:text-base"
            >
              ← Back
            </button>

            <div className="text-center mb-6 md:mb-8">
              <div className="text-5xl md:text-7xl mb-3 md:mb-4 animate-bounce">{selectedGoal.icon}</div>
              <h2 className="text-2xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">{selectedGoal.title}</h2>
              <p className="text-base md:text-xl text-gray-600 mb-2">{selectedGoal.subtitle}</p>
              <p className="text-sm md:text-lg text-blue-600 font-bold">{selectedGoal.salesMessage}</p>
            </div>

            <div className="space-y-4 md:space-y-6">
              {/* Existing Member */}
              <div className="border-2 border-blue-300 rounded-2xl p-4 md:p-6 bg-blue-50 hover:bg-blue-100 transition-colors">
                <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <span className="text-2xl md:text-3xl">🎫</span> Returning Member
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Member ID or Phone Number</label>
                    <input
                      type="text"
                      value={memberId}
                      onChange={(e) => setMemberId(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleMemberLookup()}
                      placeholder="Enter your ID or phone"
                      className="w-full p-3 md:p-4 text-base md:text-lg border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none bg-white font-medium"
                    />
                    <p className="text-xs md:text-sm text-blue-600 font-semibold mt-2 bg-blue-100 rounded-lg py-2 px-3">
                      💡 Demo pre-filled with: <span className="font-bold">12345</span>
                    </p>
                  </div>
                  <button
                    onClick={handleMemberLookup}
                    disabled={memberId.length < 4}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg md:text-xl font-bold py-3 md:py-4 rounded-xl hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 transition-all flex items-center justify-center gap-2"
                  >
                    Continue <ArrowRight size={20} />
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-gray-300"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 md:px-4 bg-gray-50 text-gray-600 text-base md:text-lg font-semibold">OR</span>
                </div>
              </div>

              {/* New Member */}
              <button
                onClick={() => setStage('new-member')}
                className="w-full border-2 border-green-300 bg-green-50 hover:bg-green-100 rounded-2xl p-4 md:p-6 transition-all group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 md:mb-2 group-hover:text-green-700 transition-colors">
                      ✨ New to RepMingle?
                    </h3>
                    <p className="text-gray-600 text-base md:text-lg">Quick registration + Single Session</p>
                  </div>
                  <ArrowRight size={28} className="text-green-600 group-hover:translate-x-2 transition-transform flex-shrink-0" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - NEW MEMBER REGISTRATION =====
  if (mode === 'kiosk' && stage === 'new-member') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8">
            <button 
              onClick={() => setStage('identify')}
              className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-colors"
            >
              ← Back
            </button>

            <div className="text-center mb-8">
              <div className="text-7xl mb-4 animate-pulse">📝</div>
              <h2 className="text-4xl font-black bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">New Member Registration</h2>
              <p className="text-gray-600 text-lg">Let's get you started with your fitness journey!</p>
              <p className="text-sm text-green-600 font-semibold mt-3 bg-green-50 rounded-lg py-2 px-3 inline-block">✅ Sample data pre-filled - Just click Continue!</p>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={newMemberData.firstName || sampleMemberData.firstName}
                    onChange={(e) => setNewMemberData({...newMemberData, firstName: e.target.value})}
                    placeholder={sampleMemberData.firstName}
                    className="w-full p-3 text-lg border-2 border-green-300 rounded-xl focus:border-blue-500 focus:outline-none font-medium bg-green-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={newMemberData.lastName || sampleMemberData.lastName}
                    onChange={(e) => setNewMemberData({...newMemberData, lastName: e.target.value})}
                    placeholder={sampleMemberData.lastName}
                    className="w-full p-3 text-lg border-2 border-green-300 rounded-xl focus:border-blue-500 focus:outline-none font-medium bg-green-50"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={newMemberData.email || sampleMemberData.email}
                  onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
                  placeholder={sampleMemberData.email}
                  className="w-full p-3 text-lg border-2 border-green-300 rounded-xl focus:border-blue-500 focus:outline-none font-medium bg-green-50"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  value={newMemberData.phone || sampleMemberData.phone}
                  onChange={(e) => setNewMemberData({...newMemberData, phone: e.target.value})}
                  placeholder={sampleMemberData.phone}
                  className="w-full p-3 text-lg border-2 border-green-300 rounded-xl focus:border-blue-500 focus:outline-none font-medium bg-green-50"
                />
              </div>

              <button
                onClick={handleNewMemberRegistration}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white text-xl font-bold py-4 rounded-xl hover:from-green-700 hover:to-green-800 transition-all flex items-center justify-center gap-2 mt-6 shadow-lg hover:shadow-xl"
              >
                Continue to Health Screening <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - HEALTH SCREENING (FIX #2 IMPROVED UX) =====
  if (mode === 'kiosk' && stage === 'health') {
    const allAnswered = Object.values(healthScreening).every(r => r !== null);
    const answeredCount = Object.values(healthScreening).filter(r => r !== null).length;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8 flex items-center justify-center">
        <div className="max-w-3xl w-full mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <button 
              onClick={() => setStage('new-member')}
              className="mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-colors"
            >
              ← Back
            </button>

            <h2 className="text-4xl font-black bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent mb-2 text-center">
              Health Screening (PAR-Q)
            </h2>
            <p className="text-center text-gray-600 mb-2">Quick safety questions (5 total)</p>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
              <div 
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                style={{width: `${(answeredCount / 5) * 100}%`}}
              ></div>
            </div>

            <div className="space-y-6">
              <HealthQuestion
                question="Do you have any heart or cardiovascular conditions?"
                value={healthScreening.heartCondition}
                onChange={(val) => setHealthScreening({...healthScreening, heartCondition: val})}
                icon="❤️"
              />
              
              <HealthQuestion
                question="Are you currently experiencing chest pain?"
                value={healthScreening.chestPain}
                onChange={(val) => setHealthScreening({...healthScreening, chestPain: val})}
                icon="🫀"
              />
              
              <HealthQuestion
                question="Do you have joint, bone, or muscle injuries?"
                value={healthScreening.jointIssues}
                onChange={(val) => setHealthScreening({...healthScreening, jointIssues: val})}
                icon="🦴"
              />
              
              <HealthQuestion
                question="Are you taking prescribed medications?"
                value={healthScreening.medications}
                onChange={(val) => setHealthScreening({...healthScreening, medications: val})}
                icon="💊"
              />
              
              <HealthQuestion
                question="Have you experienced dizziness during exercise?"
                value={healthScreening.dizziness}
                onChange={(val) => setHealthScreening({...healthScreening, dizziness: val})}
                icon="🌀"
              />
            </div>

            <button
              onClick={handleHealthScreening}
              disabled={!allAnswered}
              className="w-full mt-8 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xl font-bold py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 transition-all flex items-center justify-center gap-2"
            >
              {allAnswered ? (
                <>Continue to Payment <ArrowRight size={20} /></>
              ) : (
                `Answer all questions (${answeredCount}/5)`
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - SESSION OPTION =====
  if (mode === 'kiosk' && stage === 'session-option') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
            <button 
              onClick={resetKiosk}
              className="mb-4 md:mb-6 text-gray-500 hover:text-gray-700 flex items-center gap-2 transition-colors text-sm md:text-base"
            >
              ← Cancel
            </button>

            <div className="text-center mb-6 md:mb-8">
              <div className="text-5xl md:text-7xl mb-3 md:mb-4">💳</div>
              <h2 className="text-2xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent mb-2">Session Options</h2>
              <p className="text-gray-600 text-base md:text-lg">Choose how you'd like to proceed</p>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleSingleSessionPurchase}
                className="w-full border-2 border-green-300 bg-green-50 hover:bg-green-100 rounded-2xl p-4 md:p-6 transition-all group cursor-pointer"
              >
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                  <div className="text-left">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-1 md:mb-2 group-hover:text-green-700">🎯 Single Session</h3>
                    <p className="text-gray-600 text-sm md:text-base">30-minute training session with expert coach</p>
                  </div>
                  <div className="text-left md:text-right">
                    <div className="text-3xl md:text-4xl font-black text-green-600">${sessionPrice}</div>
                    <div className="text-xs md:text-sm text-gray-500">one-time</div>
                  </div>
                </div>
              </button>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-2xl p-4 md:p-6">
                <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  ✨ What's Included:
                </h3>
                <ul className="space-y-2 md:space-y-3 text-gray-700 text-sm md:text-base">
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                    30 minutes with certified trainer
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                    Personalized workout for: <span className="font-semibold text-blue-600">{selectedGoal.title}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-500 flex-shrink-0" />
                    Form correction & technique tips
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - PAYMENT =====
  if (mode === 'kiosk' && stage === 'payment') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4 md:p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8">
            <h2 className="text-2xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6 md:mb-8 text-center">
              Payment Information
            </h2>

            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-2xl p-4 md:p-6 mb-6 md:mb-8">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                <span className="text-base md:text-xl font-bold text-gray-800">30-Minute Training Session</span>
                <span className="text-3xl md:text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">${sessionPrice}</span>
              </div>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-3 md:p-4 mb-4 md:mb-6">
              <p className="text-center text-yellow-800 font-semibold text-sm md:text-base">
                🧪 Testing Mode: Click Pay to proceed
              </p>
            </div>

            <div className="space-y-3 md:space-y-4 mb-4 md:mb-6 opacity-50">
              <input 
                type="text" 
                placeholder="Card Number (Testing)" 
                className="w-full p-3 md:p-4 text-base md:text-xl border-2 rounded-xl bg-gray-50" 
                disabled 
              />
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                <input 
                  type="text" 
                  placeholder="MM/YY" 
                  className="w-full p-3 md:p-4 text-base md:text-xl border-2 rounded-xl bg-gray-50" 
                  disabled 
                />
                <input 
                  type="text" 
                  placeholder="CVV" 
                  className="w-full p-3 md:p-4 text-base md:text-xl border-2 rounded-xl bg-gray-50" 
                  disabled 
                />
              </div>
            </div>

            <button
              onClick={handlePayment}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white text-lg md:text-xl font-bold py-3 md:py-4 rounded-xl hover:from-green-700 hover:to-green-800 transition-all flex items-center justify-center gap-2"
            >
              Pay ${sessionPrice} <Zap size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - TIMING SELECTION =====
  if (mode === 'kiosk' && stage === 'timing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8 flex items-center justify-center">
        <div className="max-w-3xl w-full mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <h2 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 text-center">
              When would you like to start?
            </h2>
            <p className="text-center text-gray-600 text-lg mb-8">Choose your preferred timing</p>

            <div className="space-y-4">
              <TimingOption
                icon={<Zap size={40} className="text-blue-600" />}
                title="Start Training NOW"
                description="Get matched immediately"
                onClick={() => handleTimingSelection('now')}
                badge="FASTEST"
              />
              
              <TimingOption
                icon={<Clock size={40} className="text-purple-600" />}
                title="Warm up first (15 minutes)"
                description="Take time to prepare"
                onClick={() => handleTimingSelection('15min')}
              />
              
              <TimingOption
                icon={<Clock size={40} className="text-green-600" />}
                title="Complete warm-up (30 minutes)"
                description="Full preparation routine"
                onClick={() => handleTimingSelection('30min')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - MATCHING =====
  if (mode === 'kiosk' && stage === 'matching') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8 flex items-center justify-center relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500 rounded-full mix-blend-screen filter blur-3xl animate-pulse" style={{transform: 'translate(-50%, -50%)'}}></div>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-12 text-center max-w-2xl w-full relative z-10">
          <div className="mb-8">
            <div className="relative inline-block">
              <div className="animate-pulse">
                <Dumbbell size={100} className="text-blue-600" />
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center shadow-xl animate-pulse">
                <div className="text-white text-4xl font-black">{matchCountdown}</div>
              </div>
            </div>
          </div>
          
          <h2 className="text-4xl font-black bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Finding Your Perfect Match...
          </h2>
          
          <div className="space-y-3 text-lg text-gray-600 mb-6">
            <div className="flex items-center justify-center gap-3 animate-fadeIn" style={{animationDelay: '0s'}}>
              <CheckCircle size={24} className="text-green-500" />
              <span>Analyzing trainer specializations</span>
            </div>
            <div className="flex items-center justify-center gap-3 animate-fadeIn" style={{animationDelay: '0.5s'}}>
              <CheckCircle size={24} className="text-green-500" />
              <span>Checking availability</span>
            </div>
            <div className="flex items-center justify-center gap-3 animate-fadeIn" style={{animationDelay: '1s'}}>
              <CheckCircle size={24} className="text-green-500" />
              <span>Comparing ratings & experience</span>
            </div>
          </div>
          
          <div className="text-xl text-gray-500">
            Match complete in <span className="font-bold text-blue-600 text-2xl">{matchCountdown}s</span>
          </div>
        </div>
      </div>
    );
  }

  // ===== KIOSK - CONFIRMED =====
  if (mode === 'kiosk' && stage === 'confirmed' && matchedTrainer) {
    const { timeString, warmupInfo, walkTime } = calculateMeetingTime();
    const meetingLocations = ['Pilates Room A', 'Yoga Studio', 'Main Gym Floor - Station 3', 'Functional Training Area', 'Weight Room'];
    const randomLocation = meetingLocations[Math.floor(Math.random() * meetingLocations.length)];

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-8 flex items-center justify-center">
        <div className="max-w-3xl w-full mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="inline-block mb-4">
                <CheckCircle size={80} className="text-green-500 animate-pulse" />
              </div>
              <h2 className="text-4xl font-black bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">You're All Set!</h2>
              <p className="text-xl text-gray-600">Your trainer is ready to meet you</p>
            </div>

            <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-2xl p-6 mb-6 border-2 border-blue-200">
              <div className="flex items-start gap-6">
                <div className="text-7xl">{matchedTrainer.image}</div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-gray-800 mb-2">{matchedTrainer.name}</h3>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={20} 
                          className={i < Math.floor(matchedTrainer.rating) ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                        />
                      ))}
                    </div>
                    <span className="text-xl font-bold">{matchedTrainer.rating}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {matchedTrainer.specializations.slice(0, 3).map((spec, idx) => (
                      <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Session Details */}
            <div className="bg-gray-50 rounded-2xl p-6 mb-6 space-y-4 border-2 border-gray-200">
              <SessionInfoRow label="Session" value={selectedGoal.title} icon="🎯" />
              <SessionInfoRow label="Duration" value="30 minutes" icon="⏱️" />
              <SessionInfoRow label="Price" value={`$${sessionPrice}`} icon="💰" />
            </div>

            {/* Meeting Details */}
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl p-6 mb-6 border-3 border-orange-300">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                📍 Meeting Details
              </h3>
              
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-4 border-2 border-orange-200">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Warm-up Time:</p>
                  <p className="text-xl font-bold text-orange-600">{warmupInfo}</p>
                </div>

                <div className="bg-white rounded-xl p-4 border-2 border-orange-200">
                  <p className="text-sm text-gray-600 font-semibold mb-1">Meeting Location:</p>
                  <p className="text-xl font-bold text-blue-600">{randomLocation}</p>
                </div>

                <div className="bg-gradient-to-r from-green-100 to-green-50 rounded-xl p-4 border-3 border-green-400">
                  <p className="text-sm text-gray-600 font-semibold mb-2">✅ Meet at:</p>
                  <p className="text-3xl font-black text-green-600">{timeString}</p>
                  <p className="text-sm text-gray-600 font-medium mt-2">
                    ({walkTime} minutes walk time included)
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <p className="text-sm text-gray-700">
                    📝 <span className="font-bold">Meeting Details:</span><br/>
                    <span className="text-base mt-2 block">
                      Let's meet at <span className="font-bold text-blue-600">{timeString}</span> in the <span className="font-bold text-blue-600">{randomLocation}</span>.<br/>
                      {warmupInfo !== 'NO warm-up' && `Take ${warmupInfo.split(' ')[0]} minutes to warm up and then head over. `}
                      {warmupInfo === 'NO warm-up' && `Come directly! `}
                      We'll have {walkTime} minutes to get to the training area.
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={resetKiosk}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xl font-bold py-4 rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
            >
              Done <Zap size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== ADMIN MODE (FIX #3: Enhanced with better graphics) =====
  if (mode === 'admin') {
    const totalRevenue = sessions.reduce((sum, s) => sum + s.price, 0);
    const ongoingSessions = sessions.filter(s => s.status === 'ongoing').length;
    const completedToday = sessions.filter(s => s.status === 'completed').length;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          <LogoHeader showBadge={false} lightBackground={true} />
          
          <button
            onClick={() => setMode('kiosk')}
            className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg"
          >
            <LogOut size={20} />
            Exit to Kiosk
          </button>
        </div>
        
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-blue-500">
          <div>
            <h2 className="text-3xl font-black text-gray-800">Admin Dashboard</h2>
            <p className="text-gray-600 mt-1">Real-time facility overview</p>
          </div>
        </div>

        {/* BONUS #2: Enhanced dashboard cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <EnhancedStatCard 
            icon={<DollarSign />} 
            title="Total Revenue" 
            value={`$${totalRevenue}`} 
            bgColor="from-green-500 to-emerald-600"
            change="+12%"
          />
          <EnhancedStatCard 
            icon={<Activity />} 
            title="Ongoing Sessions" 
            value={ongoingSessions} 
            bgColor="from-blue-500 to-blue-600"
            change={`${ongoingSessions} active`}
          />
          <EnhancedStatCard 
            icon={<CheckCircle />} 
            title="Completed Today" 
            value={completedToday} 
            bgColor="from-purple-500 to-purple-600"
            change="so far"
          />
          <EnhancedStatCard 
            icon={<Users />} 
            title="Active Trainers" 
            value={trainers.filter(t => t.available).length} 
            bgColor="from-orange-500 to-red-600"
            change={`of ${trainers.length} total`}
          />
        </div>

        {pendingTrainers.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-orange-500">
            <h2 className="text-2xl font-black text-gray-800 mb-4 flex items-center gap-3">
              <AlertCircle size={28} className="text-orange-500" />
              Pending Trainer Approvals ({pendingTrainers.length})
            </h2>
            <div className="space-y-4">
              {pendingTrainers.map(trainer => (
                <div key={trainer.id} className="border-2 border-orange-200 bg-orange-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{trainer.name}</h3>
                      <p className="text-sm text-gray-600">{trainer.email}</p>
                      <p className="text-sm text-gray-600">{trainer.phone}</p>
                    </div>
                    <span className="px-3 py-1 bg-orange-200 text-orange-800 text-sm rounded-full font-semibold">
                      Pending Review
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Specializations:</h4>
                    <div className="flex flex-wrap gap-2">
                      {trainer.specializations.map((spec, idx) => (
                        <span key={idx} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-lg text-xs font-medium">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Certifications:</h4>
                    <div className="flex flex-wrap gap-2">
                      {trainer.certifications.map((cert, idx) => (
                        <span key={idx} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-lg text-xs font-medium">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>

                  {trainer.bio && (
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">Bio:</h4>
                      <p className="text-sm text-gray-600 italic">{trainer.bio}</p>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleApproveTrainer(trainer.id)}
                      className="flex-1 bg-gradient-to-r from-green-600 to-green-700 text-white py-3 rounded-lg hover:from-green-700 hover:to-green-800 font-bold transition-all"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleRejectTrainer(trainer.id)}
                      className="flex-1 bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-lg hover:from-red-700 hover:to-red-800 font-bold transition-all"
                    >
                      ✗ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Available Coaches</h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {trainers.map(trainer => (
                <div key={trainer.id} className="border-2 border-gray-100 rounded-xl p-4 hover:border-green-300 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3">
                      <div className="text-4xl">{trainer.image}</div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg">{trainer.name}</h3>
                        <div className="flex items-center gap-2 text-sm mb-1">
                          <Star className="text-yellow-500 fill-yellow-500" size={14} />
                          <span className="font-semibold">{trainer.rating}</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {trainer.specializations.slice(0, 2).map((spec, idx) => (
                            <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-lg font-medium">
                              {spec}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <span className={`px-4 py-1 rounded-full text-sm font-bold whitespace-nowrap ${
                      trainer.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {trainer.available ? '🟢 Available' : '🔴 Off'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Session Activity</h2>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {sessions.map(session => (
                <div key={session.id} className="border-2 border-gray-100 rounded-xl p-4 hover:border-blue-300 hover:shadow-md transition-all">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="font-bold text-gray-800">{session.member}</h3>
                      <p className="text-sm text-gray-600">with {session.trainer}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                      session.status === 'ongoing' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {session.status === 'ongoing' ? '▶️ In Progress' : '✓ Completed'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>{session.goal}</span>
                    <span className="font-bold text-gray-800">${session.price}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== TRAINER MODE - LIST =====
  if (mode === 'trainer' && trainerAuthMode === 'list') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 to-green-800 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="text-7xl mb-4">👨‍🏫</div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">Trainer Portal</h1>
              <p className="text-gray-600 text-lg">Select a trainer to manage</p>
            </div>

            <div className="space-y-4 mb-6">
              {trainers.map(trainer => (
                <button
                  key={trainer.id}
                  onClick={() => {
                    setSelectedTrainer(trainer);
                    setTrainerAuthMode('dashboard');
                  }}
                  className="w-full bg-gradient-to-r from-gray-50 to-green-50 hover:from-green-50 hover:to-green-100 border-2 border-gray-200 hover:border-green-500 rounded-2xl p-6 transition-all text-left group"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-5xl">{trainer.image}</div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 group-hover:text-green-700">{trainer.name}</h3>
                      <p className="text-sm text-gray-600">{trainer.email}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {trainer.specializations.slice(0, 3).map((spec, idx) => (
                          <span key={idx} className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-lg font-semibold">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className={`px-4 py-3 rounded-xl font-bold text-sm whitespace-nowrap ${
                      trainer.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {trainer.available ? '🟢 Available' : '🔴 Off'}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t-2 border-gray-200 pt-6 space-y-4">
              <button
                onClick={() => setTrainerAuthMode('signup')}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-bold py-3 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all"
              >
                + Register New Trainer
              </button>

              <button
                onClick={() => setMode('kiosk')}
                className="w-full text-gray-500 hover:text-gray-700 py-2 font-semibold"
              >
                ← Back to Kiosk
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== TRAINER MODE - SIGNIN / SIGNUP / DASHBOARD =====
  // (Keep existing trainer mode code - same as before)
  if (mode === 'trainer' && trainerAuthMode === 'signin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 to-green-800 p-8 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4 animate-bounce">👨‍🏫</div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Trainer Portal</h1>
            <p className="text-gray-600 text-lg font-semibold mb-4">⏱️ Your Time is Valuable</p>
            <p className="text-gray-500 italic text-sm">Just click and see how our trainers manage their schedule</p>
          </div>

          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4 mb-6">
            <p className="text-center text-blue-800 font-semibold text-sm">
              💡 Demo Access: Click below to see trainer workflow in action
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                📧 Email
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Auto-filled</span>
              </label>
              <input
                type="email"
                value={trainerEmail || 'mike.thompson@repmingle.com'}
                onChange={(e) => setTrainerEmail(e.target.value)}
                placeholder="mike.thompson@repmingle.com"
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none font-medium text-sm"
              />
              <p className="text-xs text-gray-500 mt-1 italic">💬 "No time to create an account? We got you!"</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                🔐 Password
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Auto-filled</span>
              </label>
              <input
                type="password"
                value={trainerPassword || 'trainer123'}
                onChange={(e) => setTrainerPassword(e.target.value)}
                placeholder="trainer123"
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none font-medium text-sm"
              />
              <p className="text-xs text-gray-500 mt-1 italic">💬 "One click away from seeing genius at work"</p>
            </div>

            <button
              onClick={handleTrainerSignIn}
              className="w-full bg-gradient-to-r from-green-600 to-green-700 text-white text-lg font-semibold py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl"
            >
              🔓 Trainer Login
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-4 bg-white text-gray-500 text-sm">or</span>
              </div>
            </div>

            <button
              onClick={() => setTrainerAuthMode('signup')}
              className="w-full bg-blue-600 text-white text-lg font-semibold py-3 rounded-lg hover:bg-blue-700 transition-all"
            >
              🆕 Register as Trainer
            </button>

            <button
              onClick={() => setMode('kiosk')}
              className="w-full text-gray-500 hover:text-gray-700 py-2 font-semibold text-sm"
            >
              ← Back to Kiosk
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-2">🎯 Quick Demo Credentials:</p>
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 font-mono space-y-1">
                <p>📧 mike.thompson@repmingle.com</p>
                <p>🔑 trainer123</p>
              </div>
              <p className="text-xs text-green-600 font-semibold mt-2">✓ Already filled above - just click!</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'trainer' && trainerAuthMode === 'signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-800 p-8 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Trainer Registration</h1>
              <p className="text-gray-600">Join our team of fitness professionals</p>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                  <input
                    type="text"
                    value={newTrainerData.firstName}
                    onChange={(e) => setNewTrainerData({...newTrainerData, firstName: e.target.value})}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={newTrainerData.lastName}
                    onChange={(e) => setNewTrainerData({...newTrainerData, lastName: e.target.value})}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={newTrainerData.email}
                    onChange={(e) => setNewTrainerData({...newTrainerData, email: e.target.value})}
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newTrainerData.phone}
                    onChange={(e) => setNewTrainerData({...newTrainerData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={newTrainerData.password}
                  onChange={(e) => setNewTrainerData({...newTrainerData, password: e.target.value})}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Specializations (Select all that apply)
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto border-2 border-gray-200 rounded-lg p-4">
                  {availableSpecializations.map((spec) => (
                    <label key={spec} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTrainerData.specializations.includes(spec)}
                        onChange={() => toggleSpecialization(spec)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">{spec}</span>
                    </label>
                  ))}
                </div>
                {newTrainerData.specializations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {newTrainerData.specializations.map((spec) => (
                      <span key={spec} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold">
                        {spec}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Certifications (Select all that apply)
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto border-2 border-gray-200 rounded-lg p-4">
                  {availableCertifications.map((cert) => (
                    <label key={cert} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newTrainerData.certifications.includes(cert)}
                        onChange={() => toggleCertification(cert)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">{cert}</span>
                    </label>
                  ))}
                </div>
                {newTrainerData.certifications.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {newTrainerData.certifications.map((cert) => (
                      <span key={cert} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-semibold">
                        {cert}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Bio (Optional)</label>
                <textarea
                  value={newTrainerData.bio}
                  onChange={(e) => setNewTrainerData({...newTrainerData, bio: e.target.value})}
                  placeholder="Tell members about your training philosophy and experience..."
                  rows={4}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleTrainerSignUp}
                  className="flex-1 bg-blue-600 text-white text-lg font-semibold py-3 rounded-lg hover:bg-blue-700"
                >
                  Submit Registration
                </button>
                <button
                  onClick={() => setTrainerAuthMode('signin')}
                  className="px-8 bg-gray-300 text-gray-700 text-lg font-semibold py-3 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (mode === 'trainer' && trainerAuthMode === 'dashboard' && selectedTrainer) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          <LogoHeader showBadge={false} lightBackground={true} />
          
          <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
            <button
              onClick={() => {
                setSelectedTrainer(null);
                setTrainerAuthMode('signin');
                setTrainerEmail('');
                setTrainerPassword('');
              }}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold"
            >
              Sign Out
            </button>
            <button
              onClick={() => setMode('kiosk')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
            >
              Exit to Kiosk
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-l-4 border-green-500">
          <div>
            <h2 className="text-3xl font-bold text-gray-800">Welcome, {selectedTrainer.name}!</h2>
            <p className="text-gray-600 mt-1">Manage your schedule and availability</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Availability Status Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Availability Status</h2>
            
            <div className="mb-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border-2 border-gray-200">
                <span className="font-semibold text-gray-700">Current Status:</span>
                <button
                  onClick={() => {
                    const updated = trainers.map(t => 
                      t.id === selectedTrainer.id ? {...t, available: !t.available} : t
                    );
                    setTrainers(updated);
                    setSelectedTrainer({...selectedTrainer, available: !selectedTrainer.available});
                  }}
                  className={`px-6 py-3 rounded-lg font-bold transition-all ${
                    selectedTrainer.available 
                      ? 'bg-green-500 text-white hover:bg-green-600' 
                      : 'bg-red-500 text-white hover:bg-red-600'
                  }`}
                >
                  {selectedTrainer.available ? '🟢 Available' : '🔴 Off Duty'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
                <div className="text-3xl font-black text-blue-600">{selectedTrainer.sessionsToday}</div>
                <div className="text-sm text-gray-600 font-semibold">Sessions Today</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500">
                <div className="text-3xl font-black text-green-600">${selectedTrainer.sessionsToday * 45}</div>
                <div className="text-sm text-gray-600 font-semibold">Revenue Today</div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-700 mb-2">Your Specializations</h3>
              <div className="flex flex-wrap gap-2">
                {selectedTrainer.specializations.map((spec, idx) => (
                  <span key={idx} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {spec}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Today's Schedule Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Today's Schedule</h2>

            <div className="space-y-3 mb-4">
              {selectedTrainer.schedule.length > 0 ? (
                selectedTrainer.schedule.map((slot, idx) => (
                  <div key={idx} className="border-2 border-gray-100 rounded-lg p-4 hover:border-blue-300">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-gray-800 text-lg">{slot.time}</div>
                        <div className="text-sm text-gray-600 font-semibold">{slot.client}</div>
                        {slot.type && <div className="text-xs text-gray-500 mt-1">{slot.type}</div>}
                      </div>
                      {slot.client !== 'Available' && (
                        <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full font-bold">
                          Booked
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No scheduled sessions yet
                </div>
              )}
            </div>

            <button className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
              <Plus size={20} />
              Add Scheduled Session
            </button>

            <div className="mt-6">
              <h3 className="font-semibold text-gray-700 mb-3">Block Time Off</h3>
              <div className="flex gap-2">
                <input
                  type="time"
                  className="flex-1 p-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
                <input
                  type="time"
                  className="flex-1 p-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
                <button className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold">
                  Block
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-purple-500 mt-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">🧠 Daily Mood</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">How are you training today?</label>
              <select
                value={trainerDailyFocus[selectedTrainer.id] || ''}
                onChange={(e) => setTrainerDailyFocus({
                  ...trainerDailyFocus,
                  [selectedTrainer.id]: e.target.value
                })}
                className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none font-medium"
              >
                <option value="">Select your mood...</option>
                <option value="aggressive">🔥 Aggressive (Push limits, challenge, intensity)</option>
                <option value="strength">💪 Strength Focus (Build power, lift heavy, technical)</option>
                <option value="cardio">⚡ Cardio/Conditioning (Burn, endurance, speed)</option>
                <option value="flexibility">🧘 Flexibility/Mobility (Stretch, movement quality, ease)</option>
                <option value="bodybuilding">🎯 Bodybuilding (Pump, muscle, mind-muscle connection)</option>
                <option value="functional">🏃 Functional/Athletic (Performance, sports, explosiveness)</option>
                <option value="rehabilitation">💚 Rehabilitation (Healing, careful, progress)</option>
                <option value="fun">😄 Fun/Playful (Enjoy, experiment, laugh)</option>
                <option value="custom">✏️ Custom Focus (I'll type it)</option>
              </select>
            </div>

            {trainerDailyFocus[selectedTrainer.id] === 'custom' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Your Custom Focus</label>
                <input
                  type="text"
                  placeholder="e.g., Arm biceps triceps, Olympic lifting, Core strength..."
                  value={trainerDailyRecommendation[selectedTrainer.id]?.customFocus || ''}
                  onChange={(e) => setTrainerDailyRecommendation({
                    ...trainerDailyRecommendation,
                    [selectedTrainer.id]: {
                      ...trainerDailyRecommendation[selectedTrainer.id],
                      customFocus: e.target.value
                    }
                  })}
                  className="w-full p-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ABOUT ME PAGE
  if (mode === 'about') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 p-4 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="mb-6">
            <button
              onClick={() => setMode('kiosk')}
              className="flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all font-semibold backdrop-blur-sm"
            >
              <ArrowRight size={20} className="rotate-180" />
              Back to Kiosk
            </button>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 p-6 md:p-10 text-white">
              <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                <div className="w-32 h-32 md:w-40 md:h-40 bg-white rounded-full flex items-center justify-center text-6xl md:text-7xl shadow-2xl flex-shrink-0">
                  👨‍🔬
                </div>
                <div className="text-center md:text-left">
                  <h1 className="text-3xl md:text-5xl font-black mb-2 md:mb-3">Ozkan Altun, PhD</h1>
                  <p className="text-lg md:text-2xl font-semibold mb-2 md:mb-3 opacity-90">Founder • Researcher • Innovator</p>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <span className="px-3 md:px-4 py-1 md:py-2 bg-white/20 backdrop-blur-sm rounded-full text-xs md:text-sm font-bold">PhD Sports Management</span>
                    <span className="px-3 md:px-4 py-1 md:py-2 bg-white/20 backdrop-blur-sm rounded-full text-xs md:text-sm font-bold">15+ Years Experience</span>
                    <span className="px-3 md:px-4 py-1 md:py-2 bg-white/20 backdrop-blur-sm rounded-full text-xs md:text-sm font-bold">ACSM CPT</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-6 md:p-10">
              {/* Bio Section */}
              <div className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <User className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-gray-800">About Me</h2>
                </div>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                  I drive innovation in the fitness, exercise and recreation field through research-backed solutions. With a Master's and PhD in Sports Management, I focus on physical activity intervention enabled by technology to help people move and achieve a healthy lifestyle. Over more than 15 years, I've managed operations internationally, trained hundreds of fitness professionals, operated sports, fitness, recreation and community facilities and international training academies, and built systems that solve real problems.
                </p>
              </div>

              {/* Education Section */}
              <div className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-gray-800">Education</h2>
                </div>
                <div className="space-y-4 md:space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4 md:pl-6 py-2 md:py-3 bg-blue-50 rounded-r-xl">
                    <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1 md:mb-2">PhD (2018) - Middle East Technical University (METU) </h3>
                    <p className="text-sm md:text-base text-gray-700 mb-2 md:mb-3">"Factors Contributing to User Adoption of Beacon: Technology-Supported Mobile Fitness Instructor Application"</p>
                    <a 
                      href="https://youtube.com/watch?v=9DLkiVXz2m8" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold text-sm md:text-base"
                    >
                      🎥 Watch Product Video
                    </a>
                  </div>
                  <div className="border-l-4 border-purple-500 pl-4 md:pl-6 py-2 md:py-3 bg-purple-50 rounded-r-xl">
                    <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1 md:mb-2">Master's (2009) - METU </h3>
                    <p className="text-sm md:text-base text-gray-700">"The Effectiveness of Participant-Centered Physical Activity Counseling on Sedentary Working Women"</p>
                  </div>
                </div>
              </div>

              {/* Publications Section */}
              <div className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BookOpen className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-gray-800">Selected Publications</h2>
                </div>
                <div className="space-y-3 md:space-y-4">
                  {[
                    {
                      title: "Assessing the intention to use beacon technology supported mobile fitness instructor application in fitness centers: A descriptive research",
                      journal: "Turkiye Klinikleri Journal of Sports Sciences, 17(1), 81–90",
                      year: "2025"
                    },
                    {
                      title: "The Effect of Participant-Centered Physical Activity Counseling on the Physical Activity Level of Sedentary Working Women",
                      journal: "Gazi Journal of Physical Education and Sports Sciences, 15(2), 29–39",
                      year: "2010"
                    },
                    {
                      title: "Effects of Pilates Exercise on Trunk Strength, Endurance and Flexibility in Sedentary Adult Females",
                      journal: "Journal of Bodywork and Movement Therapies, 11, 318–326",
                      year: "2007"
                    },
                    {
                      title: "Participant-Centered Physical Activity Counseling Program Followed by Phone and E-mail",
                      journal: "TOJET Conference Proceedings, Boğaziçi University, 1164–1171",
                      year: "2010"
                    }
                  ].map((pub, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-3 md:p-4 hover:shadow-lg transition-shadow">
                      <p className="text-sm md:text-base font-semibold text-gray-800 mb-1 md:mb-2">{pub.title}</p>
                      <p className="text-xs md:text-sm text-gray-600 italic">{pub.journal} ({pub.year})</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certifications Section */}
              <div className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Award className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-gray-800">Certifications</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  {[
                    "ACSM Certified Personal Trainer",
                    "Nationally Certified Pilates Teacher (NCPT)",
                    "Exercise is Medicine Credential",
                    "Innovation Accelerator – Montgomery County, MD"
                  ].map((cert, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200 rounded-xl p-3 md:p-4">
                      <CheckCircle className="text-yellow-600 flex-shrink-0" size={20} />
                      <span className="text-sm md:text-base font-semibold text-gray-800">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Technical Skills Section */}
              <div className="mb-8 md:mb-10">
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Activity className="text-white" size={24} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-gray-800">Technical & Operational Skills</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                  {[
                    "Microsoft 365",
                    "Power BI",
                    "Power Automate",
                    "Excel",
                    "SharePoint",
                    "RECTRAC",
                    "Photoshop",
                    "Premiere",
                    "Acrobat Pro",
                    "Data Visualization",
                    "Digital Marketing",
                    "Video/Photo Editing"
                  ].map((skill, idx) => (
                    <div key={idx} className="bg-indigo-100 text-indigo-800 px-2 md:px-3 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-bold text-center">
                      {skill}
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Section */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 md:p-8 text-white text-center">
                <h2 className="text-2xl md:text-3xl font-black mb-3 md:mb-4">Let's Connect</h2>
                <p className="text-base md:text-lg mb-4 md:mb-6 opacity-90">Interested in innovative fitness solutions? Let's talk!</p>
                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
                  <a 
                    href="mailto:drozaltun@gmail.com"
                    className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-all font-bold text-sm md:text-base"
                  >
                    <Mail size={20} />
                    drozaltun@gmail.com
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/sporhocam"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 px-4 md:px-6 py-2.5 md:py-3 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white rounded-lg transition-all font-bold text-sm md:text-base"
                  >
                    <Linkedin size={20} />
                    LinkedIn Profile
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// HELPER COMPONENTS
const HealthQuestion = ({ question, value, onChange, icon }) => (
  <div className="border-2 border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
    <p className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
      <span className="text-2xl">{icon}</span>
      {question}
    </p>
    <div className="flex gap-3">
      <button
        onClick={() => onChange(false)}
        className={`flex-1 py-3 px-6 rounded-lg text-lg font-bold transition-all ${
          value === false 
            ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        No
      </button>
      <button
        onClick={() => onChange(true)}
        className={`flex-1 py-3 px-6 rounded-lg text-lg font-bold transition-all ${
          value === true 
            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        Yes
      </button>
    </div>
  </div>
);

const TimingOption = ({ icon, title, description, onClick, badge }) => (
  <button
    onClick={onClick}
    className="w-full border-2 border-gray-200 rounded-2xl p-6 hover:border-blue-500 hover:bg-blue-50 transition-all text-left group relative"
  >
    {badge && (
      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold">
        {badge}
      </div>
    )}
    <div className="flex items-start gap-4">
      <div className="group-hover:scale-125 transition-transform">{icon}</div>
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-1 group-hover:text-blue-600">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  </button>
);

const EnhancedStatCard = ({ icon, title, value, bgColor, change }) => (
  <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 hover:shadow-xl transition-shadow relative overflow-hidden">
    <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${bgColor} opacity-10 rounded-full -mr-12 -mt-12`}></div>
    
    <div className="flex items-center justify-between relative z-10">
      <div className="flex-1">
        <p className="text-gray-600 text-sm font-semibold mb-1">{title}</p>
        <p className="text-4xl font-black text-gray-900">{value}</p>
        <p className="text-sm text-gray-500 font-semibold mt-1">{change}</p>
      </div>
      <div className={`bg-gradient-to-br ${bgColor} text-white p-4 rounded-2xl shadow-lg`}>
        {React.cloneElement(icon, { size: 32 })}
      </div>
    </div>
  </div>
);

const SessionInfoRow = ({ label, value, icon }) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center gap-2 text-lg font-semibold text-gray-700">
      <span className="text-2xl">{icon}</span>
      {label}
    </div>
    <div className="text-lg font-bold text-gray-900">{value}</div>
  </div>
);

export default PasswordProtectedApp;